/**
 * Generates a URL-safe, unique slug for a highlighter label.
 *
 * Normalizes the label (lowercase, strips diacritics, collapses non-alphanumerics
 * into hyphens) and, if the resulting base slug is already taken, appends
 * `-1`, `-2`, ... until a free slug is found.
 *
 * @param {string} label - Human-readable label.
 * @param {Iterable<string>} existingSlugs - Slugs already in use.
 * @returns {string} A slug not present in `existingSlugs`.
 */
export default function generateHighlighterSlug(label, existingSlugs = []) {
    const baseSlug = String(label ?? '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');

    const takenSlugs = existingSlugs instanceof Set ? existingSlugs : new Set(existingSlugs);

    if (!takenSlugs.has(baseSlug)) {
        return baseSlug;
    }

    let counter = 1;
    while (takenSlugs.has(`${baseSlug}-${counter}`)) {
        counter++;
    }
    return `${baseSlug}-${counter}`;
}
